// src/services/geocoding.service.ts

// Define types locally to avoid dependency on google.maps before it loads
export interface LatLngLiteral {
  lat: number;
  lng: number;
}

export interface LatLngBoundsLiteral {
  north: number;
  south: number;
  east: number;
  west: number;
}

export interface GeocodingResult {
  formattedAddress: string;
  location: {
    lat: number;
    lng: number;
  };
  placeId: string;
  addressComponents: {
    streetNumber?: string;
    street?: string;
    unit?: string; // Apartment/unit number
    city?: string;
    county?: string;
    state?: string;
    stateCode?: string;
    zipCode?: string;
    country?: string;
    countryCode?: string;
  };
  types: string[];
  accuracy: 'ROOFTOP' | 'RANGE_INTERPOLATED' | 'GEOMETRIC_CENTER' | 'APPROXIMATE';
  isMultiUnit?: boolean; // Flag for apartment buildings
}

export interface BatchGeocodingResult {
  input: string;
  success: boolean;
  result?: GeocodingResult;
  error?: string;
}

export interface ReverseGeocodingResult {
  formattedAddress: string;
  placeId: string;
  location: LatLngLiteral;
  addressComponents: GeocodingResult['addressComponents'];
  types: string[];
}

class GeocodingService {
  private geocoder: google.maps.Geocoder | null = null;
  private cache: Map<string, GeocodingResult> = new Map();
  private pendingRequests: Map<string, Promise<GeocodingResult>> = new Map();

  /**
   * Initialize the geocoder (call after Google Maps is loaded)
   */
  initialize(): void {
    if (window.google && window.google.maps) {
      this.geocoder = new google.maps.Geocoder();
    }
  }

  /**
   * Check if geocoder is ready
   */
  isReady(): boolean {
    return this.geocoder !== null;
  }

  /**
   * Geocode a single address to coordinates
   */
  async geocodeAddress(address: string): Promise<GeocodingResult> {
    // Check cache first
    const cached = this.cache.get(address.toLowerCase());
    if (cached) {
      return cached;
    }

    // Check if there's a pending request for this address
    const pending = this.pendingRequests.get(address.toLowerCase());
    if (pending) {
      return pending;
    }

    if (!this.geocoder) {
      this.initialize();
      if (!this.geocoder) {
        throw new Error('Geocoder not initialized. Ensure Google Maps is loaded.');
      }
    }

    const promise = new Promise<GeocodingResult>((resolve, reject) => {
      this.geocoder!.geocode({ address }, (results, status) => {
        this.pendingRequests.delete(address.toLowerCase());

        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const result = this.parseGeocoderResult(results[0]);
          this.cache.set(address.toLowerCase(), result);
          resolve(result);
        } else if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
          reject(new Error(`No results found for address: ${address}`));
        } else if (status === google.maps.GeocoderStatus.OVER_QUERY_LIMIT) {
          reject(new Error('Geocoding quota exceeded. Please try again later.'));
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });

    this.pendingRequests.set(address.toLowerCase(), promise);
    return promise;
  }

  /**
   * Batch geocode multiple addresses
   */
  async batchGeocode(
    addresses: string[],
    options: {
      delayMs?: number;
      onProgress?: (completed: number, total: number) => void;
      onError?: (address: string, error: string) => void;
    } = {}
  ): Promise<BatchGeocodingResult[]> {
    const { delayMs = 100, onProgress, onError } = options;
    const results: BatchGeocodingResult[] = [];

    for (let i = 0; i < addresses.length; i++) {
      const address = addresses[i];

      try {
        const result = await this.geocodeAddress(address);
        results.push({ input: address, success: true, result });
      } catch (error: any) {
        const errorMessage = error.message || 'Unknown error';
        results.push({ input: address, success: false, error: errorMessage });
        onError?.(address, errorMessage);
      }

      onProgress?.(i + 1, addresses.length);

      // Add delay between requests to avoid rate limiting
      if (i < addresses.length - 1) {
        await this.delay(delayMs);
      }
    }

    return results;
  }

  /**
   * Reverse geocode coordinates to address
   */
  async reverseGeocode(
    lat: number,
    lng: number
  ): Promise<ReverseGeocodingResult> {
    if (!this.geocoder) {
      this.initialize();
      if (!this.geocoder) {
        throw new Error('Geocoder not initialized. Ensure Google Maps is loaded.');
      }
    }

    return new Promise((resolve, reject) => {
      this.geocoder!.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
            const result = results[0];
            const geometry = result.geometry;
            resolve({
              formattedAddress: result.formatted_address,
              placeId: result.place_id,
              location: {
                lat: geometry.location.lat(),
                lng: geometry.location.lng(),
              },
              addressComponents: this.parseAddressComponents(result.address_components),
              types: result.types,
            });
          } else if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
            reject(new Error('No address found for these coordinates'));
          } else {
            reject(new Error(`Reverse geocoding failed: ${status}`));
          }
        }
      );
    });
  }

  /**
   * Get addresses within a polygon using multiple strategies
   * 1. Fine grid (12m) for residential density
   * 2. Staggered rows for better coverage
   * 3. Places API for additional addresses
   */
  async getAddressesInPolygon(
    polygon: LatLngLiteral[],
    gridSizeMeters: number = 12, // Default to 12m for typical lot widths
    onProgress?: (found: number, processed: number, total: number) => void
  ): Promise<GeocodingResult[]> {
    // Ensure Google Maps is loaded
    if (typeof google === 'undefined' || !google.maps) {
      throw new Error('Google Maps not loaded. Please wait for maps to initialize.');
    }

    // Calculate bounds of polygon
    const bounds = this.getPolygonBounds(polygon);
    const googlePolygon = new google.maps.Polygon({ paths: polygon });

    // Calculate area to determine optimal grid size
    const areaWidth = this.calculateDistance(
      { lat: bounds.south, lng: bounds.west },
      { lat: bounds.south, lng: bounds.east }
    );
    const areaHeight = this.calculateDistance(
      { lat: bounds.south, lng: bounds.west },
      { lat: bounds.north, lng: bounds.west }
    );
    const areaSqMeters = areaWidth * areaHeight;

    // Dynamic grid sizing - finer for small areas, coarser for large
    let effectiveGridSize = gridSizeMeters;
    if (areaSqMeters > 500000) { // > 0.5 sq km
      effectiveGridSize = Math.max(gridSizeMeters, 20);
    }
    if (areaSqMeters > 1000000) { // > 1 sq km
      effectiveGridSize = Math.max(gridSizeMeters, 30);
    }
    if (areaSqMeters > 2000000) { // > 2 sq km
      effectiveGridSize = Math.max(gridSizeMeters, 50);
    }

    // Create staggered grid points for better coverage
    const gridPoints = this.createStaggeredGridPoints(bounds, effectiveGridSize);

    // Filter points inside the polygon
    const pointsInPolygon = gridPoints.filter((point) =>
      google.maps.geometry.poly.containsLocation(
        new google.maps.LatLng(point.lat, point.lng),
        googlePolygon
      )
    );

    // Higher limit for residential areas - adjust based on area size
    const maxPoints = Math.min(
      Math.max(500, Math.floor(areaSqMeters / 200)), // ~1 point per 200 sq meters
      2000 // Hard cap
    );
    const pointsToProcess = pointsInPolygon.slice(0, maxPoints);

    if (pointsInPolygon.length > maxPoints) {
      console.warn(`Area contains ${pointsInPolygon.length} grid points, limiting to ${maxPoints}`);
    }

    // Process in parallel batches for speed
    const addresses: GeocodingResult[] = [];
    const batchSize = 10; // Process 10 at a time for speed
    const seenPlaceIds = new Set<string>();
    const seenAddresses = new Set<string>(); // Dedupe by full address too
    let processed = 0;

    for (let i = 0; i < pointsToProcess.length; i += batchSize) {
      const batch = pointsToProcess.slice(i, i + batchSize);

      const batchPromises = batch.map(async (point) => {
        try {
          const result = await this.reverseGeocode(point.lat, point.lng);

          // Accept any address type that could be residential
          const isResidential = result.types.some(t =>
            ['premise', 'street_address', 'subpremise', 'establishment', 'point_of_interest'].includes(t)
          ) || result.addressComponents.streetNumber; // Has a street number = likely a house

          if (isResidential) {
            // Create a normalized address key for deduplication
            const addressKey = `${result.addressComponents.streetNumber || ''}-${result.addressComponents.street || ''}-${result.addressComponents.zipCode || ''}`.toLowerCase();

            if (!seenPlaceIds.has(result.placeId) && !seenAddresses.has(addressKey)) {
              seenPlaceIds.add(result.placeId);
              seenAddresses.add(addressKey);

              return {
                formattedAddress: result.formattedAddress,
                location: result.location,
                placeId: result.placeId,
                addressComponents: result.addressComponents,
                types: result.types,
                accuracy: 'ROOFTOP' as const,
              };
            }
          }
          return null;
        } catch {
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(r => r && addresses.push(r));

      processed += batch.length;
      onProgress?.(addresses.length, processed, pointsToProcess.length);

      // Minimal delay between batches
      if (i + batchSize < pointsToProcess.length) {
        await this.delay(30);
      }
    }

    // Sort addresses by street then number for logical ordering
    addresses.sort((a, b) => {
      const streetA = a.addressComponents.street || '';
      const streetB = b.addressComponents.street || '';
      const streetCompare = streetA.localeCompare(streetB);
      if (streetCompare !== 0) return streetCompare;

      const numA = parseInt(a.addressComponents.streetNumber || '0', 10);
      const numB = parseInt(b.addressComponents.streetNumber || '0', 10);
      return numA - numB;
    });

    return addresses;
  }

  /**
   * Create a staggered grid for better coverage
   * Offsets every other row by half the grid size
   */
  private createStaggeredGridPoints(
    bounds: LatLngBoundsLiteral,
    gridSizeMeters: number
  ): LatLngLiteral[] {
    const points: LatLngLiteral[] = [];

    const latCenter = (bounds.north + bounds.south) / 2;
    const degreesPerMeterLat = 1 / 111320;
    const degreesPerMeterLng = 1 / (111320 * Math.cos((latCenter * Math.PI) / 180));

    const latStep = gridSizeMeters * degreesPerMeterLat;
    const lngStep = gridSizeMeters * degreesPerMeterLng;

    let rowIndex = 0;
    for (let lat = bounds.south; lat <= bounds.north; lat += latStep) {
      // Offset every other row by half the grid size for better coverage
      const lngOffset = (rowIndex % 2 === 0) ? 0 : lngStep / 2;

      for (let lng = bounds.west + lngOffset; lng <= bounds.east; lng += lngStep) {
        points.push({ lat, lng });
      }
      rowIndex++;
    }

    return points;
  }

  /**
   * Calculate distance between two points (Haversine)
   */
  private calculateDistance(p1: LatLngLiteral, p2: LatLngLiteral): number {
    const R = 6371000; // Earth radius in meters
    const lat1 = (p1.lat * Math.PI) / 180;
    const lat2 = (p2.lat * Math.PI) / 180;
    const deltaLat = ((p2.lat - p1.lat) * Math.PI) / 180;
    const deltaLng = ((p2.lng - p1.lng) * Math.PI) / 180;

    const a = Math.sin(deltaLat / 2) ** 2 +
              Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  /**
   * Get addresses within a radius of a center point
   */
  async getAddressesInRadius(
    center: LatLngLiteral,
    radiusMeters: number,
    gridSizeMeters: number = 12, // Finer default grid
    onProgress?: (found: number, processed: number, total: number) => void
  ): Promise<GeocodingResult[]> {
    // Create a circle polygon approximation
    const circlePoints = this.createCirclePolygon(center, radiusMeters, 32);
    return this.getAddressesInPolygon(circlePoints, gridSizeMeters, onProgress);
  }

  /**
   * Detect if an address is likely an apartment/multi-unit building
   */
  isMultiUnitBuilding(result: GeocodingResult): boolean {
    // Check types for multi-unit indicators
    if (result.types.some(t => ['subpremise', 'establishment'].includes(t))) {
      return true;
    }

    // Check address for apartment indicators
    const address = result.formattedAddress.toLowerCase();
    const unitIndicators = ['apt', 'apartment', 'unit', 'suite', 'ste', '#', 'building', 'bldg'];
    return unitIndicators.some(indicator => address.includes(indicator));
  }

  /**
   * Generate apartment units for a multi-unit building
   * Returns an array of addresses with unit numbers
   */
  generateApartmentUnits(
    baseAddress: GeocodingResult,
    unitCount: number = 8, // Default assumption for small apartment
    unitFormat: 'numeric' | 'alpha' | 'floor' = 'numeric'
  ): GeocodingResult[] {
    const units: GeocodingResult[] = [];
    const { streetNumber, street, city, state, stateCode, zipCode } = baseAddress.addressComponents;

    for (let i = 1; i <= unitCount; i++) {
      let unitLabel: string;

      switch (unitFormat) {
        case 'alpha':
          unitLabel = String.fromCharCode(64 + i); // A, B, C...
          break;
        case 'floor':
          // 101, 102, 201, 202, etc.
          const floor = Math.ceil(i / 4);
          const unitOnFloor = ((i - 1) % 4) + 1;
          unitLabel = `${floor}0${unitOnFloor}`;
          break;
        default:
          unitLabel = i.toString();
      }

      const unitAddress = `${streetNumber} ${street} Unit ${unitLabel}`;
      const fullAddress = `${unitAddress}, ${city}, ${stateCode} ${zipCode}`;

      units.push({
        formattedAddress: fullAddress,
        location: baseAddress.location, // Same building location
        placeId: `${baseAddress.placeId}_unit_${unitLabel}`,
        addressComponents: {
          ...baseAddress.addressComponents,
          unit: unitLabel,
        },
        types: ['subpremise', ...baseAddress.types],
        accuracy: baseAddress.accuracy,
      });
    }

    return units;
  }

  /**
   * Expand addresses to include apartment units where detected
   */
  async expandApartmentUnits(
    addresses: GeocodingResult[],
    defaultUnitCount: number = 8
  ): Promise<GeocodingResult[]> {
    const expanded: GeocodingResult[] = [];

    for (const address of addresses) {
      if (this.isMultiUnitBuilding(address)) {
        // Generate units for this building
        const units = this.generateApartmentUnits(address, defaultUnitCount);
        expanded.push(...units);
      } else {
        expanded.push(address);
      }
    }

    return expanded;
  }

  /**
   * Parse Google Maps Geocoder result
   */
  private parseGeocoderResult(result: google.maps.GeocoderResult): GeocodingResult {
    const location = result.geometry.location;
    return {
      formattedAddress: result.formatted_address,
      location: {
        lat: location.lat(),
        lng: location.lng(),
      },
      placeId: result.place_id,
      addressComponents: this.parseAddressComponents(result.address_components),
      types: result.types,
      accuracy: result.geometry.location_type as GeocodingResult['accuracy'],
    };
  }

  /**
   * Parse address components
   */
  private parseAddressComponents(
    components: google.maps.GeocoderAddressComponent[]
  ): GeocodingResult['addressComponents'] {
    const result: GeocodingResult['addressComponents'] = {};

    for (const component of components) {
      const types = component.types;

      if (types.includes('street_number')) {
        result.streetNumber = component.long_name;
      }
      if (types.includes('route')) {
        result.street = component.long_name;
      }
      if (types.includes('locality')) {
        result.city = component.long_name;
      }
      if (types.includes('administrative_area_level_2')) {
        result.county = component.long_name;
      }
      if (types.includes('administrative_area_level_1')) {
        result.state = component.long_name;
        result.stateCode = component.short_name;
      }
      if (types.includes('postal_code')) {
        result.zipCode = component.long_name;
      }
      if (types.includes('country')) {
        result.country = component.long_name;
        result.countryCode = component.short_name;
      }
    }

    return result;
  }

  /**
   * Get bounds of a polygon
   */
  private getPolygonBounds(polygon: LatLngLiteral[]): LatLngBoundsLiteral {
    let north = -Infinity;
    let south = Infinity;
    let east = -Infinity;
    let west = Infinity;

    for (const point of polygon) {
      north = Math.max(north, point.lat);
      south = Math.min(south, point.lat);
      east = Math.max(east, point.lng);
      west = Math.min(west, point.lng);
    }

    return { north, south, east, west };
  }

  /**
   * Create a grid of points within bounds
   */
  private createGridPoints(
    bounds: LatLngBoundsLiteral,
    gridSizeMeters: number
  ): LatLngLiteral[] {
    const points: LatLngLiteral[] = [];

    // Approximate degrees per meter at this latitude
    const latCenter = (bounds.north + bounds.south) / 2;
    const degreesPerMeterLat = 1 / 111320;
    const degreesPerMeterLng = 1 / (111320 * Math.cos((latCenter * Math.PI) / 180));

    const latStep = gridSizeMeters * degreesPerMeterLat;
    const lngStep = gridSizeMeters * degreesPerMeterLng;

    for (let lat = bounds.south; lat <= bounds.north; lat += latStep) {
      for (let lng = bounds.west; lng <= bounds.east; lng += lngStep) {
        points.push({ lat, lng });
      }
    }

    return points;
  }

  /**
   * Create circle polygon approximation
   */
  private createCirclePolygon(
    center: LatLngLiteral,
    radiusMeters: number,
    numPoints: number = 32
  ): LatLngLiteral[] {
    const points: LatLngLiteral[] = [];
    const earthRadius = 6371000; // meters

    for (let i = 0; i < numPoints; i++) {
      const angle = (2 * Math.PI * i) / numPoints;
      const lat =
        center.lat +
        (radiusMeters / earthRadius) * (180 / Math.PI) * Math.cos(angle);
      const lng =
        center.lng +
        ((radiusMeters / earthRadius) * (180 / Math.PI) * Math.sin(angle)) /
          Math.cos((center.lat * Math.PI) / 180);
      points.push({ lat, lng });
    }

    return points;
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Clear cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const geocodingService = new GeocodingService();
export default geocodingService;
